import {
  VTextField,
  VTextarea,
  VAutocomplete,
} from 'vuetify/components';

import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import FileUploader from '@/components/form/FileUploader.vue';
import DatePickerInput from '@/components/form/DatePickerInput.vue';
import TimePickerInput from '@/components/form/TimePickerInput.vue';

import type { FormField } from '@/types/form';

function to24hString(time12h: string, period: 'AM' | 'PM'): string {
  let [h, m] = time12h.split(':').map(Number);
  if (period === 'PM' && h < 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:00`;
}

function formatTimeValue(val: string): string {
  const match = val.match(/(\d{2}:\d{2})\s*(AM|PM)/i);
  if (!match) return '';
  return to24hString(match[1], match[2].toUpperCase() as 'AM' | 'PM');
}

export const CREATE_FORM: FormField[] = [  
  
  { value: 'only_date', label: 'Fecha', component: DatePickerInput, required: true, col: 6, range: false },
  { value: 'only_time', label: 'Horario', component: TimePickerInput, required: true, col: 6, range: false,
    transform: (val: any) => {
      if (!val) return null;
      if (Array.isArray(val)) {
        return {
          start_time: formatTimeValue(val[0]),
          end_time: formatTimeValue(val[1]),
        };
      }
      return { only_time: formatTimeValue(val) };
    }
  },

  
  { value: 'subject', 
    label: 'Cliente/Lead', 
    component: VAutocomplete, 
    required: true, 
    col: 12, 
    mdcol:12, 
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject_type: value?.type === 'leads' ? 'Lead' : 'Client',
    })
  },
  /*{
    value: 'opportunity',
    label: 'Oportunidad',
    component: VAutocomplete,
    required: false,
    col: 6,
    fetchOptions: 'searchOpportunity',
    itemText: 'opportunity_name',
    itemValue: 'id',
    dependsOn: 'subject',
    dependsBehavior: 'disabled',
    dependsCondition: { value: null, condition: 'diferent' },
    fetchParams: (form: any) => ({
      'filter[subject_id]': form?.subject.id || null
    }),
    transform: (value: any) => ({
      opportunity_id: value?.id,
    })
  },*/
  { value: 'user_id', label: 'Vendedor', component: VAutocomplete, required: true, col: 6, fetchOptions: 'searchUsers', itemText: 'name', itemValue: 'id'},
  { value: 'type', label: 'Actividad', component: VAutocomplete, required: false, col: 6, fetchOptions: 'searchActivityTypes', itemText: 'name', itemValue: 'id'},
  {
    value: 'description',
    label: 'Descripción',
    component: VTextarea,
  },
  /*{
    value: 'additional_data',
    label: 'Documentos',
    isJsonArray: true,
    plainJson: false,
    component: DynamicJsonField,
    col: 12,
    children: [
      {
        value: 'label',
        label: 'Etiqueta',
        component: VTextField,
        col: 6,
        required: false,
      },
      {
        value: 'file',
        label: 'Archivo',
        component: FileUploader,
        module: 'activities', // importante
        col: 6,
        required: false,
      }
    ],
  },*/
];

const idFieldMappings: Record<string, string> = {
  user_id: 'user',
};

export function from24hTo12h(time: string): string {
  if (!time) return '';
  const [hh, mm] = time.split(':');
  let hour = parseInt(hh, 10);
  const min = mm;
  const period = hour >= 12 ? 'PM' : 'AM';
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${String(hour).padStart(2, '0')}:${min} ${period}`;
}

// Para arreglos (rango)
export function parseTimeRange(item: any): [string, string] | null {
  if (!item) return null;
  // Espera que vengan las props start_time y end_time (ambas en 24h)
  const start = item.start_time || item.only_time;
  const end = item.end_time;
  if (start && end) {
    return [from24hTo12h(start), from24hTo12h(end)];
  }
  // Si solo es horario único (no rango)
  if (start) return [from24hTo12h(start), ''];
  return null;
}

export const EDIT_FORM: FormField[] = [...CREATE_FORM.map(field => {
  const relatedKey = idFieldMappings[field.value];
  if (relatedKey) {
    return {
      ...field,
      parse: (_: any, item: any) => item[relatedKey] || null,
      transform: (val: any) => ({
        [field.value]: val?.id ?? val
      })
    };
  }
  if (field.value === 'only_time') {
    return {
      ...field,
      parse: (_: any, item: any) => {
        // Rango
        if (item.start_time && item.end_time) {
          return [from24hTo12h(item.start_time), from24hTo12h(item.end_time)];
        }
        // Simple
        if (item.only_time) return from24hTo12h(item.only_time);
        return null;
      },
      // Mantén tu transform igual
      transform: field.transform,
    };
  }
  return field;
})];

export const HEADERS = [
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'Cliente/Lead', value: 'subject', format: 'text', sortable: false },
  { title: 'Contacto', value: 'contact', format: 'text', sortable: false },
  { title: 'Tipo', value: 'type', format: 'text', sortable: true },
  { title: 'Fecha', value: 'only_date', format: 'text', sortable: true },
  { title: 'Hora', value: 'only_time', format: 'text', sortable: true },
  { title: 'Status', value: 'status.name', format: 'text', sortable: false },
  { title: 'Responsable', value: 'user', format: 'text', sortable: false },
  { title: 'Creación', value: 'created_at', format: 'text', sortable: true },

];

export const DEFAULT_HEADER_ORDER = [
  'id',
  'subject',
  //'contact',
  'type',
  'only_date',
  'only_time',
  'status.name',
  'user',
  'created_at',
];

export const DETAIL = {
  expand_table: false,
  eye_detail: true,
  component: () => import('@/components/modules/activities/Detail.vue')
};

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'type', label: 'Actividad', component: VAutocomplete, required: false, col: 6, fetchOptions: 'searchActivityTypes', itemText: 'name', itemValue: 'id'},
  { value: 'subject', 
    label: 'Cliente/Lead', 
    component: VAutocomplete, 
    fetchOptions: 'searchClientsAndLeads', 
    itemText: (item: any) => item?.attributes?.commercial_name || item?.attributes?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      subject_id: value?.id ?? value,
      subject: value?.type === 'leads' ? 'Lead' : 'Client',
    })
  },


  { value: 'user_id', label: 'Vendedor', component: VAutocomplete, fetchOptions: 'searchUsers', itemText: 'name', itemValue: 'id'},
  { value: 'description', label: 'Descripción', component: VTextField },
];

export const CALENDAR_CONFIG = {
  dateField: 'date',
  filterField: 'date_between',
  labelField: 'type',
  colorField: 'status.color', // opcional
};